/// <reference path="../../../maplibre-xy.d.ts" />
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { toast } from '@repo/design-system/components/ui/toaster';
import { useHostMode, useHostType } from '@repo/host/hooks/use-host-info';
import { getHost } from '@repo/host/host';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectHighlightedPinIds, selectIsMiniMapMode, setActiveCategoryId } from '@repo/store/maps/slices/clientContext';
import { selectCurrentMapInfo, selectIsAddingCustomLocation, selectIsAddingMapLocation, selectIsBulkMovingLocations, selectIsEditingNewlyCreatedPin, selectIsInCreatorMode, selectIsMovingLocation, selectLayerVisibility, selectMapConfig, selectMapData, selectMapId, selectSelectedMarkerId, selectTitleSlug, selectTrackedCompassLocation, setCompleteMapData } from '@repo/store/maps/slices/mapData';
import { CatalogGameMapFlags } from '@repo/types/catalog';
import { HostMode, HostType } from '@repo/types/host';
import type { MapDataLocationFeature, MapInitializationData } from '@repo/types/maps/map-types';
import { createWemodProtocolUrl } from '@repo/types/maps/map-utils';
import { copyTextToClipboard } from '@repo/utility/clipboard';
import { hasFlag } from '@repo/utility/flags';
import * as MapLibre from 'maplibre-gl';
import { Underzoom } from 'maplibre-xy';
import { Protocol } from 'pmtiles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMapCompassEnabled } from '../../compass/use-map-compass-enabled';
import { useMapVisibleLocations } from '../../fog-of-war/useMapVisibleLocations';
import { getIsDedicatedMiniMap, getIsSessionMiniMap } from '../../utils/mini-map-mode';
import { useMapContext } from '../map-context';
import { isMapUsable, removeMapDeferred } from '../map-lifecycle';
import { getPinsInPaddedViewport, PIN_HIT_RADIUS_PX } from '../utils/pin-hit-testing';
import { withBlankMissingTiles } from '../utils/pmtiles-protocol';
import { hasVisiblePinInteractionLayer, MARKER_DOTS_LAYER_ID, MARKER_IMAGE_HIGHLIGHTED_PREFIX, MARKER_IMAGE_PREFIX } from './constants';
import { useDenseMarkerDeckRenderer } from './useDenseMarkerDeckRenderer';
import { useFilterUrlInitializer } from './useFilterUrlInitializer';
import { useFontLoader } from './useFontLoader';
import { useLayerUrlInitializer } from './useLayerUrlInitializer';
import { useMapCursor } from './useMapCursor';
import { useMapHotkeys } from './useMapHotkeys';
import { useMapPinOperations } from './useMapPinOperations';
import { useMarkerImageFactory } from './useMarkerImageFactory';
import { useRegionLabelRenderer } from './useRegionLabelRenderer';
import { useVisiblePinHitTester } from './useVisiblePinHitTester';
const TOOLTIP_HIDE_DELAY_MS = 250;
const TOOLTIP_SHOW_DELAY_MS = 250;
const MAP_RESIZE_DELAY_MS = 60;
const ICON_SCALE = 1;
const ICON_SCALE_HOVERED = 1.2;
// MapLibre renders higher symbol sort keys above lower ones; leave gaps for intermediate states.
const DEFAULT_MARKER_SORT_KEY = 1;
const ZERO_EXTERNAL_ID_MARKER_SORT_KEY = 10;
const HOVERED_MARKER_SORT_KEY = 100;
const SELECTED_MARKER_SORT_KEY = 1000;
const MARKER_SYMBOL_RENDER_LIMIT = 2500;
const MARKER_SOURCE_VIEWPORT_PADDING_RATIO = 1.25;
const defaultMarkerSortKeyExpr: MapLibre.ExpressionSpecification = ['case', ['==', ['get', 'externalId'], 0], ZERO_EXTERNAL_ID_MARKER_SORT_KEY, DEFAULT_MARKER_SORT_KEY];
const buildMarkerFilter = (selectedMarkerId: string | null): MapLibre.FilterSpecification => {
  return selectedMarkerId ? ['!=', 'externalId', selectedMarkerId] as MapLibre.FilterSpecification : ['has', 'externalId'];
};
const buildMarkerIconImage = (prefix: string): MapLibre.ExpressionSpecification => ['concat', prefix, ['case', ['==', ['has', 'color'], true], ['get', 'color'], 'default'], '-', ['get', 'subcategoryExternalId'], '-', ['case', ['==', ['get', 'found'], true], 'found', 'unfound'], '-', ['case', ['==', ['get', 'selected'], true], 'selected', 'unselected'], '-', ['case', ['==', ['get', 'verticalLevel'], 'above'], 'above', ['==', ['get', 'verticalLevel'], 'below'], 'below', 'none']];
let isPmtilesProtocolRegistered = false;
const registerPmtilesProtocol = () => {
  if (isPmtilesProtocolRegistered) return;
  const protocol = new Protocol();
  MapLibre.addProtocol('pmtiles', withBlankMissingTiles(protocol.tilev4));
  isPmtilesProtocolRegistered = true;
};
const buildHighlightedFilter = (highlightedPinIds: string[], selectedMarkerId: string | null): MapLibre.FilterSpecification => {
  if (highlightedPinIds.length === 0) return ['boolean', false];
  const include = ['in', 'externalId', ...highlightedPinIds] as MapLibre.FilterSpecification;
  return selectedMarkerId ? ['all', include, ['!=', 'externalId', selectedMarkerId]] as MapLibre.FilterSpecification : include;
};
const getLegacyTileSource = (metadata: MapInitializationData['mapData']['metadata']) => {
  const path = metadata.baseTilePath.replace(/\/$/u, '');
  return {
    type: 'xyz' as const,
    urlTemplate: metadata.tmsEnabled ? `${path}/{z}/{x}/{y}.webp` : `${path}/{z}/{y}/{x}.webp`,
    scheme: metadata.tmsEnabled ? 'tms' as const : 'xyz' as const,
    tileSize: 256,
    minZoom: metadata.minZoom,
    maxZoom: metadata.maxZoom
  };
};
const getRenderableTileSource = async (metadata: MapInitializationData['mapData']['metadata']) => {
  const tileSource = metadata.tileSource ?? getLegacyTileSource(metadata);
  if (tileSource.type !== 'pmtiles' || !tileSource.fallback) {
    return tileSource;
  }
  try {
    const response = await fetch(tileSource.url, {
      method: 'HEAD',
      signal: AbortSignal.timeout(5000)
    });
    if (response.status === 404) {
      return tileSource.fallback;
    }
  } catch {
    // A CORS/network failure here should not prevent the PMTiles protocol from trying the archive normally.
  }
  return tileSource;
};
const getMarkerSourcePadding = (map: MapLibre.Map) => {
  const container = map.getContainer();
  return Math.max(container.clientWidth, container.clientHeight) * MARKER_SOURCE_VIEWPORT_PADDING_RATIO;
};
const buildMarkerFeatureCollection = (features: readonly MapDataLocationFeature[]): GeoJSON.FeatureCollection<GeoJSON.Geometry> => ({
  type: 'FeatureCollection',
  features: features as unknown as GeoJSON.Feature[]
});
interface UseMapInitializerOptions {
  hasStoredPreferences?: boolean;
  syncSettled?: boolean;
}
export function useMapInitializer(providedMapData?: MapInitializationData, options?: UseMapInitializerOptions) {
  const dispatch = useAppDispatch();
  const isInCreatorMode = useAppSelector(selectIsInCreatorMode);
  const mapData = useAppSelector(selectMapData);
  const {
    fonts
  } = useFontLoader(mapData, {
    loadFullFonts: isInCreatorMode
  });
  const currentFontsRef = useRef(fonts);
  const renderedFontsRef = useRef(fonts);
  const markerAppearanceKey = JSON.stringify({
    categories: mapData?.metadata.categories.map(category => [category.externalId, category.color]),
    subcategories: mapData?.metadata.subcategories.map(subcategory => [subcategory.externalId, subcategory.categoryExternalId, subcategory.icon])
  });
  const renderedMarkerAppearanceKeyRef = useRef(markerAppearanceKey);
  currentFontsRef.current = fonts;
  const {
    map,
    setMap
  } = useMapContext();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const highlightedPinIds = useAppSelector(selectHighlightedPinIds);
  const trackedCompassLocation = useAppSelector(selectTrackedCompassLocation);
  const mapCompassEnabled = useMapCompassEnabled();
  const mapConfig = useAppSelector(selectMapConfig);
  const isMiniMapMode = useAppSelector(selectIsMiniMapMode);
  const isDedicatedMiniMap = getIsDedicatedMiniMap(mapConfig);
  const shouldHideMarkers = isDedicatedMiniMap || getIsSessionMiniMap(mapConfig, isMiniMapMode);
  const renderedHighlightedPinIds = useMemo(() => {
    const trackedPinId = mapCompassEnabled ? trackedCompassLocation?.properties.externalId : undefined;
    if (isDedicatedMiniMap) return trackedPinId ? [trackedPinId] : [];
    if (!trackedPinId || highlightedPinIds.includes(trackedPinId)) return highlightedPinIds;
    return [...highlightedPinIds, trackedPinId];
  }, [highlightedPinIds, isDedicatedMiniMap, mapCompassEnabled, trackedCompassLocation?.properties.externalId]);
  const mapVisibleLocations = useMapVisibleLocations();
  const {
    addMarkerImageToMap
  } = useMarkerImageFactory();
  const {
    selectMarkerInMap,
    togglePinFound
  } = useMapPinOperations();
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const isAddingCustomLocation = useAppSelector(selectIsAddingCustomLocation);
  const isAddingMapLocation = useAppSelector(selectIsAddingMapLocation);
  const isMovingLocation = useAppSelector(selectIsMovingLocation);
  const isBulkMovingLocations = useAppSelector(selectIsBulkMovingLocations);
  const mapInfo = useAppSelector(selectCurrentMapInfo);
  const titleSlug = useAppSelector(selectTitleSlug);
  const mapId = useAppSelector(selectMapId);
  const layerVisibility = useAppSelector(selectLayerVisibility);
  const selectedMarkerId = useAppSelector(selectSelectedMarkerId);
  const {
    pendingUrlFilterState,
    cleanUpFilterUrlParams
  } = useFilterUrlInitializer({
    hasStoredPreferences: options?.hasStoredPreferences,
    syncSettled: options?.syncSettled
  });
  useLayerUrlInitializer();
  const isEditingNewlyCreatedPin = useAppSelector(selectIsEditingNewlyCreatedPin);
  const hostType = useHostType();
  const hostMode = useHostMode();
  const {
    setCursor
  } = useMapCursor();
  const analytics = useAnalytics();
  const prevStyleKeyRef = useRef<string | null>(null);
  const mapInitializationRunRef = useRef(0);
  const {
    initializeHotkeys
  } = useMapHotkeys();
  const findVisiblePinAtPoint = useVisiblePinHitTester();
  useRegionLabelRenderer();
  const denseMarkerModeRef = useRef(false);
  const denseMarkerOverlayReadyRef = useRef(false);
  const hideMarkersRef = useRef(shouldHideMarkers);
  const showHighlightedMarkersWhenHiddenRef = useRef(isDedicatedMiniMap);
  const [isDenseMarkerMode, setIsDenseMarkerMode] = useState(false);
  const [denseHoveredMarkerId, setDenseHoveredMarkerId] = useState<string | null>(null);
  const hoveredMarkerIdRef = useRef<string | null>(null);
  const hoverPopupRef = useRef<MapLibre.Popup | null>(null);
  const popupDivRef = useRef<HTMLDivElement | null>(null);
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isHoveringMarkerRef = useRef(false);
  const isHoveringPopupRef = useRef(false);
  const [hoveredMarker, setHoveredMarker] = useState<MapDataLocationFeature | null>(null);
  const isEditingNewlyCreatedPinRef = useRef(isEditingNewlyCreatedPin);
  useEffect(() => {
    isEditingNewlyCreatedPinRef.current = isEditingNewlyCreatedPin;
  }, [isEditingNewlyCreatedPin]);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const [isPinLoginModalOpen, setIsPinLoginModalOpen] = useState(false);
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);
  const isAddingCustomLocationRef = useRef(isAddingCustomLocation);
  useEffect(() => {
    isAddingCustomLocationRef.current = isAddingCustomLocation;
  }, [isAddingCustomLocation]);
  const isAddingMapLocationRef = useRef(isAddingMapLocation);
  useEffect(() => {
    isAddingMapLocationRef.current = isAddingMapLocation;
  }, [isAddingMapLocation]);
  const isMovingLocationRef = useRef(isMovingLocation);
  useEffect(() => {
    isMovingLocationRef.current = isMovingLocation;
  }, [isMovingLocation]);
  const isBulkMovingLocationsRef = useRef(isBulkMovingLocations);
  useEffect(() => {
    isBulkMovingLocationsRef.current = isBulkMovingLocations;
  }, [isBulkMovingLocations]);
  const isInCreatorModeRef = useRef(isInCreatorMode);
  useEffect(() => {
    isInCreatorModeRef.current = isInCreatorMode;
  }, [isInCreatorMode]);
  const applyMarkerLayerVisibility = useCallback((targetMap: MapLibre.Map) => {
    if (!isMapUsable(targetMap)) {
      return;
    }
    const hideMarkers = hideMarkersRef.current;
    const denseMarkerMode = denseMarkerModeRef.current;
    const hideMapLibreMarkersForDenseMode = denseMarkerMode && denseMarkerOverlayReadyRef.current;
    if (targetMap.getLayer('markers')) {
      targetMap.setLayoutProperty('markers', 'visibility', hideMarkers || hideMapLibreMarkersForDenseMode ? 'none' : 'visible');
    }
    if (targetMap.getLayer(MARKER_DOTS_LAYER_ID)) {
      targetMap.setLayoutProperty(MARKER_DOTS_LAYER_ID, 'visibility', hideMarkers || !denseMarkerMode ? 'none' : 'visible');
    }
    if (targetMap.getLayer('selected-marker')) {
      targetMap.setLayoutProperty('selected-marker', 'visibility', hideMarkers || hideMapLibreMarkersForDenseMode ? 'none' : 'visible');
    }
    if (targetMap.getLayer('markers-highlighted')) {
      targetMap.setLayoutProperty('markers-highlighted', 'visibility', hideMarkers && !showHighlightedMarkersWhenHiddenRef.current || hideMapLibreMarkersForDenseMode ? 'none' : 'visible');
    }
  }, []);
  const handleDenseMarkerOverlayReadyChange = useCallback((ready: boolean) => {
    if (denseMarkerOverlayReadyRef.current === ready) return;
    denseMarkerOverlayReadyRef.current = ready;
    if (map && isMapInitialized) {
      applyMarkerLayerVisibility(map);
    }
  }, [applyMarkerLayerVisibility, isMapInitialized, map]);
  useDenseMarkerDeckRenderer({
    enabled: isMapInitialized && isDenseMarkerMode && !shouldHideMarkers,
    map,
    mapData,
    locations: mapVisibleLocations,
    fonts,
    highlightedPinIds: renderedHighlightedPinIds,
    hoveredMarkerId: denseHoveredMarkerId,
    selectedMarkerId,
    onReadyChange: handleDenseMarkerOverlayReadyChange
  });
  useEffect(() => {
    if (!isDenseMarkerMode) {
      setDenseHoveredMarkerId(null);
    }
  }, [isDenseMarkerMode]);
  const updateMarkerSource = useCallback((targetMap_0: MapLibre.Map | null | undefined, locations: readonly MapDataLocationFeature[]) => {
    if (!isMapUsable(targetMap_0)) {
      return;
    }
    const source = targetMap_0.getSource('markers') as MapLibre.GeoJSONSource | undefined;
    if (!source) return;
    const sourceLocations = getPinsInPaddedViewport(targetMap_0, locations, getMarkerSourcePadding(targetMap_0));
    source.setData(buildMarkerFeatureCollection(sourceLocations));
    const denseMarkerMode_0 = locations.length > MARKER_SYMBOL_RENDER_LIMIT;
    if (denseMarkerMode_0 !== denseMarkerModeRef.current && !denseMarkerMode_0) {
      denseMarkerOverlayReadyRef.current = false;
    }
    denseMarkerModeRef.current = denseMarkerMode_0;
    setIsDenseMarkerMode(denseMarkerMode_0);
    applyMarkerLayerVisibility(targetMap_0);
  }, [applyMarkerLayerVisibility]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: mapId intentionally triggers reset of category lens on map navigation
  useEffect(() => {
    dispatch(setActiveCategoryId(null));
  }, [mapId, dispatch]);
  const lastClickTime = useRef<number>(0);
  const CLICK_DEBOUNCE_MS = 100;
  useEffect(() => {
    if (providedMapData) {
      try {
        dispatch(setCompleteMapData({
          mapData: providedMapData.mapData,
          mapInfo: providedMapData.mapInfo,
          mapConfig: providedMapData.mapConfig
        }));
      } catch {
        // TODO: Handle error
      }
    }
  }, [dispatch, providedMapData]);
  useEffect(() => {
    if (!isMapInitialized || isBulkMovingLocations || !isMapUsable(map)) {
      return;
    }
    const refreshMarkerSource = () => updateMarkerSource(map, mapVisibleLocations);
    refreshMarkerSource();
    map.on('moveend', refreshMarkerSource);
    map.on('resize', refreshMarkerSource);
    return () => {
      map.off('moveend', refreshMarkerSource);
      map.off('resize', refreshMarkerSource);
    };
  }, [mapVisibleLocations, isMapInitialized, map, isBulkMovingLocations, updateMarkerSource]);
  useEffect(() => {
    if (!isMapInitialized || !isMapUsable(map)) {
      return;
    }
    if (map.getLayer('markers')) {
      map.setFilter('markers', buildMarkerFilter(selectedMarkerId));
    }
    if (map.getLayer('markers-highlighted')) {
      map.setFilter('markers-highlighted', buildHighlightedFilter(renderedHighlightedPinIds, selectedMarkerId));
    }
    if (map.getLayer('selected-marker')) {
      map.setLayoutProperty('selected-marker', 'icon-image', buildMarkerIconImage(selectedMarkerId && renderedHighlightedPinIds.includes(selectedMarkerId) ? MARKER_IMAGE_HIGHLIGHTED_PREFIX : MARKER_IMAGE_PREFIX));
    }
  }, [isMapInitialized, map, renderedHighlightedPinIds, selectedMarkerId]);
  useEffect(() => {
    if (isMapInitialized && isMapUsable(map) && map.getLayer('map-tiles')) {
      map.setLayoutProperty('map-tiles', 'visibility', layerVisibility.rasterTiles ? 'visible' : 'none');
    }
  }, [map, isMapInitialized, layerVisibility.rasterTiles]);

  // Hide ordinary pins in focused mini-map views while preserving a tracked Compass destination.
  useEffect(() => {
    hideMarkersRef.current = shouldHideMarkers;
    showHighlightedMarkersWhenHiddenRef.current = isDedicatedMiniMap;
    if (!map || !isMapInitialized) return;
    applyMarkerLayerVisibility(map);
  }, [applyMarkerLayerVisibility, isDedicatedMiniMap, isMapInitialized, map, shouldHideMarkers]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want limit re-render triggers on main map initialization
  useEffect(() => {
    let isCancelled = false;
    const initializationRun = ++mapInitializationRunRef.current;
    const isCurrentInitialization = () => !isCancelled && mapInitializationRunRef.current === initializationRun;
    const initializeMap = async () => {
      const container = mapContainerRef.current;
      if (!container || !mapData || !fonts) return;
      const metadata = mapData.metadata;
      const styleKey = `${JSON.stringify(metadata.tileSource)}|${metadata.baseTilePath}|${metadata.tmsEnabled}|${metadata.minZoom}|${metadata.maxZoom}`;

      // Only reset the map if style-critical metadata changed
      if (map && isMapInitialized && prevStyleKeyRef.current === styleKey) {
        return; // skip re-init; sources will update via setData effect
      }
      const tileSource = await getRenderableTileSource(metadata);
      if (!isCurrentInitialization() || mapContainerRef.current !== container) {
        return;
      }
      if (map) {
        setIsMapInitialized(false);
        setMap(null);
      }
      if (tileSource.type === 'pmtiles') {
        registerPmtilesProtocol();
      }
      const underzoom = new Underzoom(MapLibre, {
        extendScale: 0.9,
        extendPan: 0.2
      });
      const loadedMap = new MapLibre.Map({
        container: mapContainerRef.current,
        style: {
          version: 8,
          glyphs: '/maps/static/fonts/pbf/{fontstack}/{range}.pbf',
          sources: {
            'map-tiles': tileSource.type === 'pmtiles' ? {
              type: 'raster',
              url: `pmtiles://${tileSource.url}`,
              tileSize: tileSource.tileSize,
              minzoom: tileSource.minZoom ?? metadata.minZoom,
              maxzoom: tileSource.maxZoom ?? metadata.maxZoom
            } : {
              type: 'raster',
              tiles: [tileSource.urlTemplate],
              tileSize: tileSource.tileSize,
              minzoom: tileSource.minZoom ?? metadata.minZoom,
              maxzoom: tileSource.maxZoom ?? metadata.maxZoom,
              scheme: tileSource.scheme
            }
          },
          layers: [{
            id: 'background',
            type: 'background',
            paint: {
              'background-color': metadata.backgroundColor ?? '#000000'
            }
          }, {
            id: 'map-tiles',
            type: 'raster',
            source: 'map-tiles',
            paint: {
              'raster-fade-duration': 0
            }
          }]
        },
        center: [0, 0],
        zoom: metadata.initialZoom ?? metadata.minZoom,
        renderWorldCopies: false,
        maxZoom: isInCreatorMode ? null : metadata.maxZoom - 1,
        minZoom: metadata.minZoom - 1,
        attributionControl: false,
        boxZoom: false,
        dragRotate: true,
        dragPan: !isDedicatedMiniMap,
        keyboard: !isDedicatedMiniMap,
        doubleClickZoom: !isDedicatedMiniMap,
        scrollZoom: isDedicatedMiniMap ? {
          around: 'center'
        } : true,
        touchZoomRotate: isDedicatedMiniMap ? {
          around: 'center'
        } : true,
        touchPitch: !isDedicatedMiniMap,
        trackResize: false,
        transformConstrain: underzoom.transformConstrain
      });
      if (isDedicatedMiniMap) {
        loadedMap.touchZoomRotate.disableRotation();
      }

      // Wrap queryRenderedFeatures to guard against MapLibre's dictionary_coder
      // crash when a tile's symbol index hasn't loaded yet. Layer-specific event
      // listeners (mousemove, mouseenter, mouseleave) trigger an internal
      // queryRenderedFeatures call that is not wrapped in try/catch, causing the
      // map to freeze with "Out of bounds" errors on every mouse move.
      const originalQueryRenderedFeatures = loadedMap.queryRenderedFeatures.bind(loadedMap);
      loadedMap.queryRenderedFeatures = (...args: Parameters<typeof loadedMap.queryRenderedFeatures>): ReturnType<typeof loadedMap.queryRenderedFeatures> => {
        try {
          return originalQueryRenderedFeatures(...args);
        } catch (e) {
          if (e instanceof Error && e.message.toLowerCase().includes('out of bounds')) {
            return [] as unknown as ReturnType<typeof loadedMap.queryRenderedFeatures>;
          }
          throw e;
        }
      };
      setMap(loadedMap);
      loadedMap.on('load', () => {
        if (!isCurrentInitialization()) {
          removeMapDeferred(loadedMap);
          return;
        }
        prevStyleKeyRef.current = styleKey;
        const initialMarkerSourceLocations = getPinsInPaddedViewport(loadedMap, mapVisibleLocations, getMarkerSourcePadding(loadedMap));
        const initialDenseMarkerMode = mapVisibleLocations.length > MARKER_SYMBOL_RENDER_LIMIT;
        denseMarkerModeRef.current = initialDenseMarkerMode;
        setIsDenseMarkerMode(initialDenseMarkerMode);
        loadedMap.addSource('markers', {
          type: 'geojson',
          data: buildMarkerFeatureCollection(initialMarkerSourceLocations),
          promoteId: 'externalId'
        });
        loadedMap.addSource('selected-marker', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          },
          promoteId: 'externalId'
        });
        const layoutProperties: MapLibre.SymbolLayerSpecification['layout'] = {
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-image': buildMarkerIconImage(MARKER_IMAGE_PREFIX),
          'symbol-sort-key': defaultMarkerSortKeyExpr,
          'symbol-z-order': 'source'
        };
        const selectedLayoutProperties: MapLibre.SymbolLayerSpecification['layout'] = {
          ...layoutProperties,
          'icon-image': buildMarkerIconImage(selectedMarkerId && renderedHighlightedPinIds.includes(selectedMarkerId) ? MARKER_IMAGE_HIGHLIGHTED_PREFIX : MARKER_IMAGE_PREFIX),
          'symbol-sort-key': SELECTED_MARKER_SORT_KEY
        };
        loadedMap.addLayer({
          id: MARKER_DOTS_LAYER_ID,
          type: 'circle',
          source: 'markers',
          layout: {
            visibility: 'none'
          },
          paint: {
            'circle-color': '#ffffff',
            'circle-radius': PIN_HIT_RADIUS_PX,
            'circle-opacity': 0,
            'circle-stroke-opacity': 0
          }
        });
        loadedMap.addLayer({
          id: 'markers',
          type: 'symbol',
          source: 'markers',
          layout: layoutProperties,
          filter: buildMarkerFilter(selectedMarkerId)
        });
        const highlightedLayoutProperties: MapLibre.SymbolLayerSpecification['layout'] = {
          ...layoutProperties,
          'icon-image': buildMarkerIconImage(MARKER_IMAGE_HIGHLIGHTED_PREFIX)
        };
        loadedMap.addLayer({
          id: 'markers-highlighted',
          type: 'symbol',
          source: 'markers',
          layout: highlightedLayoutProperties,
          filter: buildHighlightedFilter(renderedHighlightedPinIds, selectedMarkerId)
        });
        loadedMap.addLayer({
          id: 'selected-marker',
          type: 'symbol',
          source: 'selected-marker',
          layout: selectedLayoutProperties
        });
        applyMarkerLayerVisibility(loadedMap);
        const triggerWiggleAnimation = () => {
          if (isEditingNewlyCreatedPinRef.current) {
            const locationInfoElement = document.getElementById('location-info');
            if (locationInfoElement) {
              locationInfoElement.style.animation = 'wiggle 0.3s ease-in-out';
              setTimeout(() => {
                locationInfoElement.style.animation = '';
              }, 500);
            }
          }
        };
        const handleTooltipHideDelay = () => {
          if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
          }
          hideTimeoutRef.current = setTimeout(() => {
            if (isHoveringMarkerRef.current || isHoveringPopupRef.current) {
              return;
            }
            hoveredMarkerIdRef.current = null;
            setDenseHoveredMarkerId(null);
            setHoveredMarker(null);
            if (hoverPopupRef.current) {
              hoverPopupRef.current.remove();
              hoverPopupRef.current = null;
              popupDivRef.current = null;
            }
          }, TOOLTIP_HIDE_DELAY_MS);
        };
        const clearHoveredMarker = ({
          resetCursor = true
        }: {
          resetCursor?: boolean;
        } = {}) => {
          const previousHoveredId = hoveredMarkerIdRef.current;
          const hadHover = isHoveringMarkerRef.current || !!previousHoveredId || !!showTimeoutRef.current || !!hoverPopupRef.current;
          isHoveringMarkerRef.current = false;
          hoveredMarkerIdRef.current = null;
          setDenseHoveredMarkerId(null);
          if (previousHoveredId && isMapUsable(loadedMap)) {
            loadedMap.setFeatureState({
              source: 'markers',
              id: previousHoveredId
            }, {
              hovered: false
            });
            if (!denseMarkerModeRef.current) {
              loadedMap.setLayoutProperty('markers', 'icon-size', ICON_SCALE);
              loadedMap.setLayoutProperty('markers', 'symbol-sort-key', defaultMarkerSortKeyExpr);
              if (loadedMap.getLayer('markers-highlighted')) {
                loadedMap.setLayoutProperty('markers-highlighted', 'icon-size', ICON_SCALE);
                loadedMap.setLayoutProperty('markers-highlighted', 'symbol-sort-key', defaultMarkerSortKeyExpr);
              }
            }
            if (showTimeoutRef.current) {
              clearTimeout(showTimeoutRef.current);
              showTimeoutRef.current = null;
            }
          }
          if (resetCursor) {
            setCursor(isDedicatedMiniMap ? 'default' : 'grab');
          }
          if (hadHover) {
            handleTooltipHideDelay();
          }
        };
        const applyHoveredMarkerLayout = (hoveredId: string) => {
          if (denseMarkerModeRef.current || !isMapUsable(loadedMap)) {
            return;
          }
          const iconSizeExpr: MapLibre.ExpressionSpecification = ['case', ['==', ['get', 'selected'], true], ICON_SCALE,
          // Leave selected markers at default scale
          ['==', ['get', 'externalId'], hoveredId], ICON_SCALE_HOVERED,
          // Scale up non-selected hovered marker
          ICON_SCALE // default value
          ];
          const sortKeyExpr: MapLibre.ExpressionSpecification = ['case', ['==', ['get', 'externalId'], hoveredId], HOVERED_MARKER_SORT_KEY, ['==', ['get', 'externalId'], 0], ZERO_EXTERNAL_ID_MARKER_SORT_KEY, DEFAULT_MARKER_SORT_KEY];
          loadedMap.setLayoutProperty('markers', 'icon-size', iconSizeExpr);
          loadedMap.setLayoutProperty('markers', 'symbol-sort-key', sortKeyExpr);
          if (loadedMap.getLayer('markers-highlighted')) {
            loadedMap.setLayoutProperty('markers-highlighted', 'icon-size', iconSizeExpr);
            loadedMap.setLayoutProperty('markers-highlighted', 'symbol-sort-key', sortKeyExpr);
          }
        };
        const showHoveredMarkerTooltip = (marker: MapDataLocationFeature) => {
          if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
          }
          showTimeoutRef.current = setTimeout(() => {
            if (!isHoveringMarkerRef.current || marker.geometry.type !== 'Point') {
              return;
            }
            const [lng, lat] = marker.geometry.coordinates;
            if (hoverPopupRef.current) {
              hoverPopupRef.current.remove();
              popupDivRef.current = null;
            }
            const popupDiv = document.createElement('div');
            popupDivRef.current = popupDiv;
            hoverPopupRef.current = new MapLibre.Popup({
              closeButton: false,
              closeOnClick: false,
              className: 'location-hover-tooltip',
              maxWidth: '400px',
              focusAfterOpen: false,
              offset: [0, -70],
              anchor: 'top'
            }).setLngLat([lng, lat]).setDOMContent(popupDiv).addTo(loadedMap);
            setHoveredMarker(marker);
            const popupEl = hoverPopupRef.current.getElement();
            if (popupEl) {
              popupEl.addEventListener('mouseenter', () => {
                isHoveringPopupRef.current = true;
              });
              popupEl.addEventListener('mouseleave', () => {
                isHoveringPopupRef.current = false;
                handleTooltipHideDelay();
              });
            }
          }, TOOLTIP_SHOW_DELAY_MS);
        };
        const prewarmSelectedMarkerImage = (marker_0: MapDataLocationFeature) => {
          const currentFonts = currentFontsRef.current;
          if (!currentFonts) return;
          const verticalLevel = marker_0.properties.verticalLevel === 'above' || marker_0.properties.verticalLevel === 'below' ? marker_0.properties.verticalLevel : null;
          const verticalLevelId = verticalLevel ?? 'none';
          const color = marker_0.properties.color ?? 'default';
          const subcategory_0 = marker_0.properties.subcategoryExternalId ?? '';
          const found = marker_0.properties.found === true;
          const selectedImageId = `${MARKER_IMAGE_PREFIX}${color}-${subcategory_0}-${found ? 'found' : 'unfound'}-selected-${verticalLevelId}`;
          if (loadedMap.hasImage(selectedImageId)) return;
          addMarkerImageToMap(selectedImageId, color, subcategory_0, found, true, currentFonts, loadedMap, verticalLevel);
        };
        const handleMapMarkerMove = (e_0: MapLibre.MapMouseEvent) => {
          if (isAddingCustomLocationRef.current || isAddingMapLocationRef.current || isMovingLocationRef.current || isBulkMovingLocationsRef.current) {
            clearHoveredMarker({
              resetCursor: false
            });
            return;
          }
          if ('buttons' in e_0.originalEvent && e_0.originalEvent.buttons !== 0) {
            clearHoveredMarker({
              resetCursor: false
            });
            return;
          }
          const closestPoint = hasVisiblePinInteractionLayer(loadedMap) ? findVisiblePinAtPoint(loadedMap, e_0.point, PIN_HIT_RADIUS_PX) : null;
          if (!closestPoint) {
            clearHoveredMarker();
            return;
          }
          e_0.preventDefault();
          isHoveringMarkerRef.current = true;
          setCursor('pointer');
          prewarmSelectedMarkerImage(closestPoint);
          if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
          }
          const newHoveredId = closestPoint.properties.externalId;
          const prevHoveredId = hoveredMarkerIdRef.current;
          if (newHoveredId === prevHoveredId) {
            return;
          }
          if (prevHoveredId) {
            loadedMap?.setFeatureState({
              source: 'markers',
              id: prevHoveredId
            }, {
              hovered: false
            });
          }
          loadedMap?.setFeatureState({
            source: 'markers',
            id: newHoveredId
          }, {
            hovered: true
          });
          hoveredMarkerIdRef.current = newHoveredId;
          setDenseHoveredMarkerId(denseMarkerModeRef.current ? newHoveredId : null);
          applyHoveredMarkerLayout(newHoveredId);
          showHoveredMarkerTooltip(closestPoint);
        };
        const handleMapMarkerRightClick = async (e_1: MapLibre.MapMouseEvent) => {
          if (isBulkMovingLocationsRef.current) return;
          const closestPoint_0 = hasVisiblePinInteractionLayer(loadedMap) ? findVisiblePinAtPoint(loadedMap, e_1.point, PIN_HIT_RADIUS_PX) : null;
          if (!closestPoint_0) return;
          e_1.preventDefault();
          e_1.originalEvent.stopPropagation();
          if (isEditingNewlyCreatedPinRef.current) {
            triggerWiggleAnimation();
            return;
          }
          if (isInCreatorModeRef.current) {
            const protocolUrl = createWemodProtocolUrl({
              titleId: mapInfo?.titleId,
              mapId: mapInfo?.id,
              locationId: closestPoint_0.properties.externalId
            });
            const clipboardResult = await copyTextToClipboard(protocolUrl);
            if (clipboardResult.success) {
              toast({
                title: 'Pin link copied to clipboard',
                variant: 'success'
              });
            } else {
              toast({
                title: 'Failed to copy link',
                variant: 'error'
              });
            }
            return;
          }
          const now = Date.now();
          if (now - lastClickTime.current < CLICK_DEBOUNCE_MS) {
            return;
          }
          lastClickTime.current = now;
          if (!isAuthenticatedRef.current) {
            setIsPinLoginModalOpen(true);
            return;
          }
          const externalId = closestPoint_0.properties.externalId;
          if (externalId) {
            void togglePinFound(externalId, 'right-click', closestPoint_0.properties.found ?? false);
          }
        };
        const handleStyleImageMissing = (e_2: MapLibre.MapStyleImageMissingEvent) => {
          const currentFonts_0 = currentFontsRef.current;
          if (!currentFonts_0 || !loadedMap) return;
          const highlighted = e_2.id.startsWith(MARKER_IMAGE_HIGHLIGHTED_PREFIX);
          if (!highlighted && !e_2.id.startsWith(MARKER_IMAGE_PREFIX)) return;
          const parts = e_2.id.split('-');
          const verticalLevel_0 = parts[parts.length - 1];
          const selected = parts[parts.length - 2];
          const found_0 = parts[parts.length - 3];
          const subcategoryStart = highlighted ? 3 : 2;
          const subcategory_1 = parts.slice(subcategoryStart, parts.length - 3).join('-');
          const color_0 = parts[highlighted ? 2 : 1];
          addMarkerImageToMap(e_2.id, color_0 ?? 'default', subcategory_1 ?? '', found_0 === 'found', selected === 'selected', currentFonts_0, loadedMap, verticalLevel_0 === 'above' || verticalLevel_0 === 'below' ? verticalLevel_0 : null, highlighted);
          if (!highlighted && selected !== 'selected') {
            const selectedImageId_0 = `${MARKER_IMAGE_PREFIX}${color_0 ?? 'default'}-${subcategory_1 ?? ''}-${found_0}-selected-${verticalLevel_0}`;
            if (!loadedMap.hasImage(selectedImageId_0)) {
              addMarkerImageToMap(selectedImageId_0, color_0 ?? 'default', subcategory_1 ?? '', found_0 === 'found', true, currentFonts_0, loadedMap, verticalLevel_0 === 'above' || verticalLevel_0 === 'below' ? verticalLevel_0 : null);
            }
          }
        };
        const hotkeyCleanup = initializeHotkeys(loadedMap);
        const handleCanvasMouseLeave = () => clearHoveredMarker();
        loadedMap.on('styleimagemissing', handleStyleImageMissing);
        loadedMap.on('contextmenu', handleMapMarkerRightClick);
        loadedMap.on('mousemove', handleMapMarkerMove);
        loadedMap.getCanvas().addEventListener('mouseleave', handleCanvasMouseLeave);
        if (hotkeyCleanup) {
          loadedMap.on('remove', hotkeyCleanup);
        }
        loadedMap.on('remove', () => {
          loadedMap.off('contextmenu', handleMapMarkerRightClick);
          loadedMap.off('mousemove', handleMapMarkerMove);
          loadedMap.getCanvas().removeEventListener('mouseleave', handleCanvasMouseLeave);
        });
        void getHost().mapEvent('render_success');
        if (mapConfig?.externalId) void getHost().setCurrentMapId(mapConfig.externalId);
        const url = new URL(window.location.href);
        const locationParam = url.searchParams.get('location');
        if (locationParam) {
          const feature = mapData.geojson.features.find(f => f.properties.externalId === locationParam);
          if (feature && feature.geometry.type === 'Point') {
            const [lng_0, lat_0] = feature.geometry.coordinates;
            if (!isInCreatorMode) {
              loadedMap.flyTo({
                center: [lng_0, lat_0],
                zoom: Math.max(loadedMap.getZoom(), 12),
                duration: 1500
              });
            }
            selectMarkerInMap(locationParam);
          }
        }
        setIsMapInitialized(true);
        setCursor(isDedicatedMiniMap ? 'default' : 'grab');
        if (analytics && mapInfo?.externalId && hostType) {
          analytics.event('game_map_ready', {
            mapId: mapInfo.externalId,
            supportsTeleport: Boolean(mapData.metadata.supportsTeleport),
            titleId: mapInfo.titleId || '',
            titleName: mapInfo.name || '',
            isEmbed: hostMode === HostMode.EMBED && hostType === HostType.WEB,
            platform: hostType,
            hasLiveLocationEnabled: hasFlag(mapInfo.flags, CatalogGameMapFlags.SupportsLiveLocation),
            titleSlug: titleSlug ?? undefined
          });
        }
      });
    };
    void initializeMap();
    return () => {
      isCancelled = true;
    };
    // We intentionally restrict dependencies to avoid re-init loops.
  }, [mapData, fonts]);
  useEffect(() => {
    const previousFonts = renderedFontsRef.current;
    const previousMarkerAppearanceKey = renderedMarkerAppearanceKeyRef.current;
    renderedFontsRef.current = fonts;
    renderedMarkerAppearanceKeyRef.current = markerAppearanceKey;
    if (!map || !isMapInitialized || !fonts || !previousFonts) return;
    if (previousFonts === fonts && previousMarkerAppearanceKey === markerAppearanceKey) return;
    for (const imageId of map.listImages()) {
      if (imageId.startsWith(MARKER_IMAGE_PREFIX)) {
        map.removeImage(imageId);
      }
    }
  }, [fonts, isMapInitialized, map, markerAppearanceKey]);
  useEffect(() => {
    if (!map || !mapData) return;
    const maxZoom = isInCreatorMode ? null : mapData.metadata.maxZoom - 1;
    map.setMaxZoom(maxZoom);
  }, [isInCreatorMode, map, mapData]);

  // Resize after layout settles. Resizing MapLibre continuously while the
  // container is changing forces raster tile coverage to churn, which reads
  // as tile flicker during sidebar/window resize.
  useEffect(() => {
    if (!map) return;
    const el = mapContainerRef.current;
    if (!el) return;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const scheduleResize = () => {
      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(() => {
        timeout = null;
        map.resize();
      }, MAP_RESIZE_DELAY_MS);
    };
    map.resize();
    const observer = new ResizeObserver(scheduleResize);
    observer.observe(el);
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
      observer.disconnect();
    };
  }, [map]);
  return {
    isMapInitialized,
    mapContainerRef,
    popupDivRef,
    hoveredMarker,
    pendingUrlFilterState,
    cleanUpFilterUrlParams,
    isPinLoginModalOpen,
    setIsPinLoginModalOpen
  };
}